<template>
  <TText size="lg" tag="h1" variant="semibold" classes="mb-4">{{
    t('auth.registrationTraveler.email.title')
  }}</TText>
  <div v-if="currentState === 'ERROR'" class="my-4">
    <ApiResponseError :api-response-error="apiResponseError" />
  </div>
  <UForm
    ref="form"
    :schema="schema"
    :state="state"
    class="space-y-3"
    @submit="onSubmit"
  >
    <UFormGroup required name="emailAddress">
      <UInput
        v-model="state.emailAddress"
        type="email"
        size="lg"
        :placeholder="t('auth.register.email')"
        :aria-label="t('auth.register.email')"
      />
    </UFormGroup>
    <UFormGroup required name="emailAddressRepeat">
      <UInput
        v-model="state.emailAddressRepeat"
        type="email"
        size="lg"
        :placeholder="t('auth.register.emailConfirm')"
        :aria-label="t('auth.register.emailConfirm')"
        onpaste="return false"
        ondrop="return false"
        autocomplete="off"
      />
    </UFormGroup>
    <UFormGroup required name="password">
      <TPassword
        v-model="state.password"
        :placeholder="t('auth.register.password')"
      />
    </UFormGroup>
    <UFormGroup required name="firstName">
      <UInput
        v-model="state.firstName"
        size="lg"
        :placeholder="t('auth.register.name')"
        :aria-label="t('auth.register.name')"
      />
    </UFormGroup>
    <UFormGroup required name="lastName">
      <UInput
        v-model="state.lastName"
        size="lg"
        :placeholder="t('auth.register.surnames')"
        :aria-label="t('auth.register.surnames')"
      />
    </UFormGroup>
    <UFormGroup required name="phone">
      <UInput
        v-model="state.phone"
        size="lg"
        type="tel"
        :placeholder="t('auth.register.phone')"
        :aria-label="t('auth.register.phone')"
      />
    </UFormGroup>
    <UFormGroup v-if="provinces && provinces.length > 0" name="provinceId">
      <UInputMenu
        v-model.number="state.provinceId"
        :options="provinces"
        size="lg"
        :placeholder="`${t('auth.register.province') + ' ' + t('form.optional')}`"
        option-attribute="name"
        value-attribute="value"
      />
    </UFormGroup>
    <UFormGroup name="allowComms">
      <UCheckbox
        v-model="state.allowComms"
        class="mt-5"
        :label="t('auth.register.allowComms')"
      />
    </UFormGroup>
    <UFormGroup name="privacyPolicy">
      <UCheckbox v-model="state.privacyPolicy" required>
        <template #label>
          <i18n-t keypath="auth.confirmation.label" tag="span" scope="global">
            <template #useConditions>
              <a
                class="underline"
                target="_blank"
                :href="t('auth.confirmation.useConditionsUrl')"
                >{{ t('auth.confirmation.useConditions') }}</a
              >
            </template>
            <template #privacyPolicy>
              <a
                class="underline"
                target="_blank"
                :href="t('auth.confirmation.privacyPolicyUrl')"
                >{{ t('auth.confirmation.privacyPolicy') }}</a
              >
            </template>
          </i18n-t>
        </template>
      </UCheckbox>
    </UFormGroup>
    <UFormGroup name="recaptcha">
      <TRecaptcha
        ref="vueRecaptchaRef"
        v-model="state.recaptcha"
        class="mt-6 mb-3"
      />
    </UFormGroup>

    <div class="flex flex-col items-center">
      <UButton
        class="mt-2 mb-6"
        variant="concrete"
        size="xl"
        type="submit"
        :disabled="!isValid"
        :loading="currentState === 'SENDING'"
      >
        {{ t('auth.register.submit') }}
      </UButton>
    </div>
  </UForm>
</template>

<script setup lang="ts">
import { z } from 'zod'
import fetchSignUp from '~/modules/api/rurality/v3/users/sign-up'
import fetchLogin from '~/modules/api/rurality/v3/users/login'
import { paramsSchema } from '~/modules/api/rurality/v3/users/sign-up/paramsSchema'
import { useZodValidator } from '~/modules/form/composables/useZodValidator'
import { scrollToTop } from '~/composables/useAnimations'
import getProvinces from '~/modules/api/rurality/v3/provinces/detail'
import { useToProvinces } from '~/composables/transformers/useToProvinces'
import { signUpOrigin } from '~/modules/auth/constants'
import loggedUserRedirect from '~/modules/auth/composables/useAuthRedirect'

interface ChildComponent {
  triggerReset(): void
}

const props = defineProps<{
  isModal?: boolean
}>()

const emit = defineEmits(['login-success'])

const apiResponseError: Ref<ApiResponseError | null> = ref(null)
const vueRecaptchaRef = ref<ChildComponent | null>(null)

const provincesResponse = await getProvinces()
const provincesRaw = provincesResponse.success
  ? provincesResponse.success.data.provinces
  : []

const provinces = useToProvinces().fromRegister(provincesRaw)

const recaptchaReset = () => {
  state.recaptcha = undefined
  vueRecaptchaRef.value?.triggerReset()
}

const { validatePhone, validatePassword, validateRequiredCheckbox } =
  useZodValidator()

const form = ref()

const schemaRaw = paramsSchema.extend({
  firstName: z.string().min(2).max(30),
  lastName: z.string().min(2).max(30),
  phone: validatePhone()
    .transform(value => (value === '' ? null : value))
    .transform(value => (value === undefined ? null : value)),
  provinceId: z
    .number()
    .int()
    .optional()
    .transform(value => (value === undefined ? null : value)),
  emailAddress: z.string().email().min(5).max(254),
  password: validatePassword(),
  privacyPolicy: validateRequiredCheckbox(),
})

const state = reactive({
  firstName: undefined,
  lastName: undefined,
  phone: undefined,
  emailAddress: undefined,
  emailAddressRepeat: undefined,
  provinceId: undefined,
  password: undefined,
  allowComms: false,
  privacyPolicy: false,
  recaptcha: undefined,
  facebookId: null,
  googleId: null,
  appleId: null,
  userType: 'TRAVELER',
  signUpOrigin: signUpOrigin.PAGINA_REGISTRO_MAIL,
})

const schema = useNuxtUiFormRepeat({
  form,
  state,
  schema: schemaRaw,
  parentFieldName: 'emailAddress',
  repeatFieldName: 'emailAddressRepeat',
  message: t('zodI18nCustom.email_repeat'),
})

const states = {
  IDLE: 'IDLE',
  INVALID: 'INVALID',
  VALID: 'VALID',
  SENDING: 'SENDING',
  ERROR: 'ERROR',
}

const currentState: Ref<keyof typeof states> = ref('IDLE')

const isValid = computed(() => {
  return schema.safeParse(state).success
})

async function onSubmit(event: any) {
  const result = schema.safeParse(event.data)

  if (result.success) {
    currentState.value = 'SENDING'

    const parsedData = result.data

    const signUp = await fetchSignUp(parsedData)

    if (signUp.success && signUp.success.data) {
      const login = await fetchLogin({
        emailAddress: parsedData.emailAddress,
        password: parsedData.password,
      })
      if (login.success && login.success.data) {
        if (props.isModal) {
          emit('login-success')
        } else {
          navigateTo(
            loggedUserRedirect({
              userType: login.success.data.userType,
              userId: login.success.data.userId,
            }),
            {
              external: true,
            },
          )
        }
      } else {
        apiResponseError.value = login.error
        currentState.value = 'ERROR'
        scrollToTop()
        recaptchaReset()
      }
    } else {
      apiResponseError.value = signUp.error
      currentState.value = 'ERROR'
      scrollToTop()
      recaptchaReset()
    }
  }
}
</script>
